<script setup lang="ts">
import moment from "moment"

import type { Order } from "@Modules/shoppingcart/Data/Models/OrderModel"
import { cancellationReasonKey } from "@Modules/shoppingcart/Helpers/cancellationReasonKey"
import { currencyFormat } from "@Helpers/support"
import useTranslation from "@Composables/useTranslation"

//
// Setup
const { __ } = useTranslation()

//
// Props
interface Props {
  order: Order | Record<string, never>
  orderId: number
  loading: boolean
  locale: string
  orderFailed: boolean
  total: { amount?: number; currency?: string } | null
  translatedTotalQuantity: string
  pdfUrl: string
  goBack: () => void
  recoverNow: () => Promise<void>
}

const props = defineProps<Props>()

//
// Methods
function hasOrder(order: Order | Record<string, never>): order is Order {
  return Object.keys(order).length > 0
}

function formatDate(date: string, format = "L"): string {
  return moment.utc(date).format(format)
}

function formatCurrency(amount: number | null, currency: string | null): string {
  if (amount === null || currency === null) return "--"
  return currencyFormat(amount, currency, props.locale)
}
</script>

<template>
  <div
    data-test-id="org_order_view"
    class="container-fluid my-5"
  >
    <div class="row mb-2">
      <div class="col-md-6">
        <h5 class="text-muted">
          Orders Management
        </h5>
        <h2>View Order</h2>
      </div>
      <div
        v-if="hasOrder(order)"
        class="col-md-6 text-right"
      >
        <button
          v-if="order.canRecover?.()"
          type="button"
          class="pull-right btn btn-blue _retry"
          @click="recoverNow()"
        >
          Retry
        </button>
        <a
          v-if="order.isFinalized?.()"
          :href="pdfUrl"
          role="button"
          class="btn btn-outline-secondary _download_pdf"
        >
          Download PDF
        </a>
      </div>
    </div>
    <div class="row mb-2">
      <div class="col-md-3">
        <b>ORDER NUMBER</b>
      </div>
      <div class="col-md-9 _order_number">
        <template v-if="!hasOrder(order)">
          --
        </template>
        <template v-else>
          #{{ String(orderId).padStart(4, "0") }}
        </template>
      </div>
    </div>
    <div class="row mb-2">
      <div class="col-md-3">
        <b>ORDER DATE</b>
      </div>
      <div class="col-md-9 _order_date">
        <template v-if="!hasOrder(order)">
          --
        </template>
        <template v-else>
          {{ formatDate(order.updated_at) }}
        </template>
      </div>
    </div>
    <div class="row mb-2">
      <div class="col">
        <p class="font-weight-semibold mb-2">
          ORDER SUMMARY
        </p>
        <div class="card card-body p-0 shadow-none overflow-hidden mb-2">
          <div class="table-responsive">
            <table class="table table-striped table-valign-middle mb-0">
              <thead>
                <tr class="text-white bg-blue-dark">
                  <th>Id</th>
                  <th>Description</th>
                  <th>Date</th>
                  <th>Status</th>
                  <th>Amount</th>
                  <th>Discount</th>
                  <th>Tax</th>
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                <template v-if="loading">
                  <tr>
                    <td
                      class="py-4 text-center"
                      colspan="8"
                    >
                      <i class="fa fa-spinner fa-spin" />
                      Loading...
                    </td>
                  </tr>
                </template>
                <template v-else-if="hasOrder(order) && order.items">
                  <tr
                    v-for="item in order.items"
                    :key="item.id"
                  >
                    <td class="td-fit _item_id">
                      {{ order.id }}
                    </td>
                    <td class="td-fit">
                      {{ item.title }}
                    </td>
                    <td class="td-fit _item_date">
                      {{ formatDate(order.updated_at, "MM/DD/YYYY") }}
                    </td>
                    <td class="td-fit">
                      <div
                        v-if="item.cancelled"
                        class="text-warning _cancellation_reason"
                        data-test-id="cancellation-reason"
                      >
                        {{ __(cancellationReasonKey(item)) }}
                      </div>
                      <div
                        v-else-if="item.processed"
                        class="text-success"
                      >
                        Success
                      </div>
                      <div
                        v-else-if="order.isOnReview?.()"
                        class="text-lime-dark"
                      >
                        Fraud review in progress
                      </div>
                      <div
                        v-else-if="order.isFailed?.()"
                        class="text-danger"
                      >
                        {{ order.statusLabel?.() ?? "Failed" }}
                      </div>
                      <div
                        v-else
                        class="text-secondary"
                      >
                        Processing
                      </div>
                    </td>
                    <td
                      class="td-fit"
                      :class="{ 'text-muted': item.cancelled || orderFailed }"
                    >
                      {{ item.quantity }}
                      <small class="text-muted">&#x2715;</small>
                      {{ formatCurrency(item.price?.amount ?? null, item.price?.currency ?? null) }}
                    </td>
                    <td
                      class="td-fit"
                      :class="{ 'text-muted': item.cancelled || orderFailed }"
                    >
                      {{ formatCurrency(item.discount?.amount ?? null, item.discount?.currency ?? null) }}
                    </td>
                    <td
                      class="td-fit"
                      :class="{ 'text-muted': item.cancelled || orderFailed }"
                    >
                      {{ formatCurrency(item.tax?.amount ?? null, item.tax?.currency ?? null) }}
                    </td>
                    <td
                      class="td-fit"
                      :class="{ 'text-hide': item.cancelled || orderFailed }"
                    >
                      {{ formatCurrency(item.total?.amount ?? null, item.total?.currency ?? null) }}
                    </td>
                  </tr>
                  <tr v-if="!orderFailed">
                    <td colspan="7">
                      <div class="d-flex justify-content-between">
                        <span class="text-muted">
                          {{ translatedTotalQuantity }}
                        </span>
                        <span class="font-weight-semibold">FINAL TOTAL</span>
                      </div>
                    </td>
                    <td class="font-weight-semibold">
                      {{ formatCurrency(total?.amount ?? null, total?.currency ?? null) }}
                    </td>
                  </tr>
                </template>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
    <div class="row mb-2">
      <div class="col-12 mb-4">
        <a
          href="#"
          class="_back"
          @click.prevent="goBack"
        >
          <i class="fa-solid fa-arrow-left mr-1" />
          Back to Orders
        </a>
      </div>
    </div>
  </div>
</template>
